// src/components/dashboard/AIExtractionPanel.tsx
import { useState, useEffect, type ReactNode } from 'react';
import type { BookingJob, GearItem, StockStatus } from '@/lib/types/workflow';
import { WorkflowStatus } from '@/lib/types/workflow';
import { SkeletonCard } from '@/components/ui/SkeletonCard';
import { brand, fonts, styles } from '@/lib/tokens';
import { fetchAiUsage, type AiUsageData } from '@/lib/services/bookings';

type Props = {
  booking: BookingJob;
};

export function AIExtractionPanel({ booking }: Props) {
  const isProcessing = booking.status === WorkflowStatus.PROCESSING;

  return (
    <section
      className="flex flex-col overflow-hidden panel-col"
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${brand.hairlineSoft}`,
        overflow: 'hidden',
        background: 'rgba(0,0,0,0.15)',
      }}
    >
      {/* Panel header */}
      <div className="panel-header">AI Extraction &amp; Matching</div>

      {/* Scrollable content */}
      <div
        className="custom-scroll"
        style={{ flex: 1, overflowY: 'auto', padding: 20 }}
      >
        {isProcessing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SkeletonCard height={72} />
            <SkeletonCard height={72} />
            <SkeletonCard height={72} />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {booking.gear.map((item) => (
                <GearItemCard key={item.id} item={item} />
              ))}
            </div>

            {/* Confidence row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: `1px solid ${brand.hairlineSoft}`,
                paddingTop: 12,
                marginTop: 12,
                gap: 12,
              }}
            >
              <span
                style={{
                  ...styles.monoLabel,
                  letterSpacing: '0.06em',
                  minWidth: 0,
                }}
              >
                Dates: {booking.rentalDates} · Location: {booking.location}
              </span>
              <span
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 10,
                  letterSpacing: '0.06em',
                  color: confidenceColor(booking.aiConfidence),
                  whiteSpace: 'nowrap',
                }}
              >
                Confidence: {booking.aiConfidence}%
              </span>
            </div>

            <AiUsageBadge draftId={booking.draftId} />
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Gear item card
// ---------------------------------------------------------------------------

function GearItemCard({ item }: { item: GearItem }) {
  const opacity = item.stockStatus === 'UNAVAILABLE' ? 0.65 : 1;

  return (
    <div
      className="card-border"
      style={{
        padding: 12,
        borderRadius: 12,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        background: brand.surface2,
        marginBottom: 12,
        opacity,
      }}
    >
      {/* Icon box */}
      <div className="gear-icon-box">
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
          {iconForItem(item.name)}
        </span>
      </div>

      {/* Info */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        {/* Row 1: name + verified icon */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <span
            style={{
              fontFamily: fonts.sans,
              fontSize: 13,
              fontWeight: 600,
              color: brand.onSurface,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {item.name}
          </span>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 16, color: brand.monoDim, flexShrink: 0 }}
          >
            {item.stockStatus === 'SUBSTITUTION' ? 'auto_awesome' : 'verified'}
          </span>
        </div>

        {/* Row 2: chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            background:    brand.hairlineSoft,
            border:        `1px solid ${brand.hairline}`,
            color:         brand.onSurfaceVariant,
            padding:       '2px 8px',
            borderRadius:  4,
            fontFamily:    fonts.mono,
            fontSize:      9,
            textTransform: 'uppercase',
          }}>
            ×{item.quantity}
          </span>
          <StockChip status={item.stockStatus} />
        </div>

        {/* Row 3: substitution note */}
        {item.stockStatus === 'SUBSTITUTION' && item.substitutionNote && (
          <span
            style={{
              color:      brand.monoDim,
              fontSize:   11,
              fontStyle:  'italic',
              fontFamily: fonts.sans,
            }}
          >
            Match: {item.substitutionNote}
          </span>
        )}
      </div>
    </div>
  );
}

function StockChip({ status }: { status: StockStatus }) {
  switch (status) {
    case 'IN_STOCK':     return <span className="chip-in-stock">In Stock</span>;
    case 'LOW_STOCK':    return <span className="chip-low-stock">Low Stock</span>;
    case 'SUBSTITUTION': return <span className="chip-substitution">Substitution</span>;
    case 'UNAVAILABLE':  return <span className="chip-unavailable">Unavailable</span>;
  }
}

function confidenceColor(value: number): string {
  if (value > 85) return brand.success;
  if (value >= 60) return brand.accent;
  return brand.error;
}

function iconForItem(name: string): string {
  const n = name.toLowerCase();
  if (/camera|alexa|cinema|red|komodo|raptor/.test(n))       return 'videocam';
  if (/lens|prime|zoom|anamorphic|summilux|zeiss|cooke/.test(n)) return 'lens';
  if (/battery|power|bebob|anton/.test(n))                   return 'battery_full';
  if (/gimbal|stabiliser|rs3|o.connor|head|sticks/.test(n))  return 'sports_motorsports';
  if (/monitor|display/.test(n))                             return 'monitor';
  if (/audio|mic|sound/.test(n))                             return 'mic';
  if (/lighting|led panel|profoto|light/.test(n))            return 'light_mode';
  return 'devices';
}

// ---------------------------------------------------------------------------
// AI Usage Badge — compact interactive chip showing extraction telemetry
// ---------------------------------------------------------------------------

function AiUsageBadge({ draftId }: { draftId: string | null | undefined }) {
  const [usage, setUsage] = useState<AiUsageData | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!draftId) return;
    fetchAiUsage(draftId)
      .then(setUsage)
      .catch(() => {});
  }, [draftId]);

  if (!usage || !usage.status) return null;

  const totalTokens = (usage.input_tokens ?? 0) + (usage.output_tokens ?? 0);
  const costZar = usage.estimated_cost_zar
    ? `R${Number(usage.estimated_cost_zar).toFixed(2)}`
    : 'R0.00';
  const durationS = usage.duration_ms != null ? `${(usage.duration_ms / 1000).toFixed(1)}s` : '0.0s';
  const modelShort = usage.model_name ?? 'unknown';

  const chipLabel = `✦ ${modelShort} · ${totalTokens.toLocaleString()} tokens · ${costZar} · ${durationS}`;

  const dotColor =
    usage.status === 'SUCCESS'
      ? 'var(--color-success)'
      : usage.status === 'CIRCUIT_OPEN'
      ? 'var(--color-warning)'
      : 'var(--color-error)';

  let recordedAt = '—';
  if (usage.recorded_at) {
    try {
      recordedAt = new Date(usage.recorded_at).toLocaleString('en-ZA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Africa/Johannesburg',
      });
    } catch {
      recordedAt = usage.recorded_at;
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 0',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: 10,
            color: brand.monoDim,
            letterSpacing: '0.02em',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {chipLabel}
        </span>
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: 14,
            color: brand.monoDim,
            flexShrink: 0,
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          expand_more
        </span>
      </button>

      {open && (
        <div
          style={{
            marginTop: 6,
            padding: '10px 12px',
            background: brand.surface2,
            border: `1px solid ${brand.hairline}`,
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <UsageRow label="Model" value={modelShort} />
          <UsageRow label="Input tokens" value={(usage.input_tokens ?? 0).toLocaleString()} />
          <UsageRow label="Output tokens" value={(usage.output_tokens ?? 0).toLocaleString()} />
          <UsageRow label="Cost" value={costZar} />
          <UsageRow label="Duration" value={durationS} />
          <UsageRow
            label="Status"
            value={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: dotColor,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                {usage.status}
              </span>
            }
          />
          <UsageRow label="Recorded at" value={recordedAt} />
        </div>
      )}
    </div>
  );
}

function UsageRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          fontFamily: fonts.mono,
          fontSize: 9,
          color: brand.monoDarker,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span style={{ fontFamily: fonts.mono, fontSize: 10, color: brand.onSurface, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}
